// dSpr Reports - `/dSpr_Reports`
//
// Admin-only audit page. Per cycle: 4 stat cards, filter row, paginated
// list of employees with their submission status + completion, detail
// drawer with full Q&A breakdown, exports (list Excel + per-employee
// PDF and Excel via the shared generator).
//
// Phase 1's generateSelfReviewPdf is the single source of truth for the
// PDF - this page just hits /api/dspr/reports/review/:id/pdf which calls
// that generator on the server.
import React, { useEffect, useState, useCallback, useRef } from "react";
// Swal removed - themed alerts via useDsprAlerts() (see utils/dSprAlerts.js).
import { useNavigate } from "react-router-dom";
import { apiFetch } from "./utils/api";
import { useDsprAlerts } from "./utils/dSprAlerts";
import { fmtDate as fmtDateShort, fmtDateTimeIST } from "./utils/dSpr_DateFormat";
import logo_eagle from "./assets/img/logo_eagle.png";
import { DSPR_QUESTION_SCHEMA } from "./dSpr_question_schema";
import "./dSpr.css";
import "./dSpr_Reports.css";

const STATUS_META = {
    "Submitted":    { label: "Submitted",   bg: "#dcfce7", fg: "#15803d", dot: "#15803d" },
    "In Progress":  { label: "In progress", bg: "#fef3c7", fg: "#92400e", dot: "#c2840a" },
    "Not Started":  { label: "Not started", bg: "#f1f5f9", fg: "#475569", dot: "#94a3b8" },
};
const STATUS_OPTIONS = [
    { value: "",            label: "Any status" },
    { value: "Submitted",   label: "Submitted" },
    { value: "In Progress", label: "In progress" },
    { value: "Not Started", label: "Not started" },
];

const SECTION_META = {
    team: { dot: "#3a5ba0", tint: "#eef2f9" },
    ops:  { dot: "#4f7b3f", tint: "#eef4ec" },
    cust: { dot: "#2c8a86", tint: "#e9f4f3" },
    grow: { dot: "#6b5ca0", tint: "#f3f1f6" },
};

const RATING_LABELS = {
    1: "Did not meet expectations",
    2: "Partially met expectations",
    3: "Met expectations",
    4: "Exceeded expectations",
    5: "Consistently exceeded expectations",
};

// Date formatters live in src/utils/dSpr_DateFormat.js - imported above.
function fmtBytes(b) {
    if (b == null) return "-";
    if (b < 1024) return `${b} B`;
    if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
    return `${(b / 1024 / 1024).toFixed(1)} MB`;
}

function DSprReports({ navSize }) {
    const navigate = useNavigate();
    const { showAlert, AlertsHost } = useDsprAlerts();

    const [cycles, setCycles] = useState([]);
    const [selectedCycle, setSelectedCycle] = useState("");
    const [managers, setManagers] = useState([]);
    const [stats, setStats] = useState({ total_assigned: 0, submitted: 0, in_progress: 0, not_started: 0 });

    const [filters, setFilters] = useState({ manager: "", status: "", search: "" });
    const [appliedFilters, setAppliedFilters] = useState({ manager: "", status: "", search: "" });

    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [pageSize] = useState(50);
    const [loading, setLoading] = useState(true);

    const [selectedReviewId, setSelectedReviewId] = useState(null);
    const [drawerData, setDrawerData] = useState(null);
    const [drawerLoading, setDrawerLoading] = useState(false);

    const [exporting, setExporting] = useState(null);

    // ── Auth + initial load ─────────────────────────────────────────────────
    useEffect(() => {
        (async () => {
            try {
                const res = await apiFetch("/api/employee/logined_employee", { credentials: "include" });
                const data = await res.json();
                if (data?.message === "Access Denied. No Token Provided!" || data?.message === "Invalid Token") {
                    navigate("/login"); return;
                }
                const me = Array.isArray(data) ? data[0] : data;
                if (!me?.emp_id) { navigate("/login"); return; }
                // Role redirect removed - ProtectedRoute (App.js) gates via matrix.
            } catch { navigate("/login"); return; }
            await loadCycles();
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadCycles = async () => {
        try {
            const res = await apiFetch("/api/dspr/reports/cycles", { credentials: "include" });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || "Couldn't load cycles");
            setCycles(data);
            // Q6 default: most recent Active, else most recent overall
            const active = data.find(c => c.status === "Active") || data[0];
            if (active) setSelectedCycle(active.id);
        } catch (e) {
            showAlert("error", "Couldn't load cycles", e.message);
        }
    };

    // ── When cycle changes - reload stats + managers + reset filters + fetch list ─
    useEffect(() => {
        if (!selectedCycle) return;
        (async () => {
            await Promise.all([loadStats(), loadManagers()]);
            const empty = { manager: "", status: "", search: "" };
            setFilters(empty);
            setAppliedFilters(empty);
            setPage(1);
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedCycle]);

    const loadStats = async () => {
        try {
            const res = await apiFetch(`/api/dspr/reports/stats?cycle=${encodeURIComponent(selectedCycle)}`, { credentials: "include" });
            const data = await res.json();
            if (res.ok) setStats(data);
        } catch { /* tolerated */ }
    };
    const loadManagers = async () => {
        try {
            const res = await apiFetch(`/api/dspr/reports/managers?cycle=${encodeURIComponent(selectedCycle)}`, { credentials: "include" });
            const data = await res.json();
            if (res.ok) setManagers(data);
        } catch { /* tolerated */ }
    };

    // ── Build query string ──────────────────────────────────────────────────
    const buildQuery = useCallback((extra = {}) => {
        const q = new URLSearchParams({
            cycle:    selectedCycle,
            page:     String(page),
            pageSize: String(pageSize),
        });
        if (appliedFilters.manager) q.set("manager", appliedFilters.manager);
        if (appliedFilters.status)  q.set("status",  appliedFilters.status);
        if (appliedFilters.search)  q.set("search",  appliedFilters.search);
        Object.entries(extra).forEach(([k, v]) => q.set(k, v));
        return q.toString();
    }, [selectedCycle, appliedFilters, page, pageSize]);

    // ── Fetch list ─────────────────────────────────────────────────────────
    const fetchRows = useCallback(async () => {
        if (!selectedCycle) return;
        setLoading(true);
        try {
            const res = await apiFetch(`/api/dspr/reports/list?${buildQuery()}`, { credentials: "include" });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || "Couldn't load list");
            setRows(Array.isArray(data.rows) ? data.rows : []);
            setTotal(data.total || 0);
        } catch (e) {
            showAlert("error", "Couldn't load list", e.message);
            setRows([]); setTotal(0);
        } finally {
            setLoading(false);
        }
    }, [buildQuery, selectedCycle]);

    useEffect(() => { fetchRows(); }, [fetchRows]);

    // ── Filter actions ─────────────────────────────────────────────────────
    const applyFilters = () => {
        setPage(1);
        setAppliedFilters(filters);
    };
    const clearFilters = () => {
        const empty = { manager: "", status: "", search: "" };
        setFilters(empty);
        setAppliedFilters(empty);
        setPage(1);
    };

    // ── Drawer ─────────────────────────────────────────────────────────────
    const openDrawer = async (reviewId) => {
        setSelectedReviewId(reviewId);
        setDrawerLoading(true);
        setDrawerData(null);
        try {
            const res = await apiFetch(`/api/dspr/reports/review/${encodeURIComponent(reviewId)}`, { credentials: "include" });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || "Couldn't load detail");
            setDrawerData(data);
        } catch (e) {
            showAlert("error", "Couldn't load detail", e.message);
            setSelectedReviewId(null);
        } finally {
            setDrawerLoading(false);
        }
    };
    const closeDrawer = () => { setSelectedReviewId(null); setDrawerData(null); };

    useEffect(() => {
        if (!selectedReviewId) return;
        const onKey = (e) => { if (e.key === "Escape") closeDrawer(); };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [selectedReviewId]);

    // ── Download helpers (trigger blob download) ───────────────────────────
    const downloadFile = async (url, fallbackName) => {
        const res = await apiFetch(url, { credentials: "include" });
        if (!res.ok) {
            let msg = "Export failed";
            try { const j = await res.json(); msg = j.error || msg; } catch { /* */ }
            throw new Error(msg);
        }
        const blob = await res.blob();
        // Try to read Content-Disposition for filename
        const cd = res.headers.get("Content-Disposition") || "";
        const m = cd.match(/filename="([^"]+)"/);
        const filename = m ? m[1] : fallbackName;
        const dl = document.createElement("a");
        dl.href = URL.createObjectURL(blob);
        dl.download = filename;
        dl.click();
        setTimeout(() => URL.revokeObjectURL(dl.href), 1500);
    };

    const exportListExcel = async () => {
        setExporting("list-excel");
        try {
            const q = new URLSearchParams({ cycle: selectedCycle });
            if (appliedFilters.manager) q.set("manager", appliedFilters.manager);
            if (appliedFilters.status)  q.set("status",  appliedFilters.status);
            if (appliedFilters.search)  q.set("search",  appliedFilters.search);
            await downloadFile(`/api/dspr/reports/list-excel?${q}`, `dspr-reports-${selectedCycle}.xlsx`);
            showAlert("success", "Exported");
        } catch (e) {
            showAlert("error", "List export failed", e.message);
        } finally {
            setExporting(null);
        }
    };

    // Smart row download - hits the /download endpoint which streams either:
    //   • response PDF (if no employee uploads)
    //   • ZIP containing PDF + uploads (otherwise)
    // Server stamps the right Content-Disposition so the saved file name +
    // extension are correct without the frontend needing to know what to fetch.
    const exportRowDownload = async (row) => {
        if (row.computed_status === "Not Started") return;
        setExporting(`download-${row.review_id}`);
        try {
            await downloadFile(
                `/api/dspr/reports/review/${encodeURIComponent(row.review_id)}/download`,
                `${row.employee_id}_response`  // fallback only - server sets the real name
            );
        } catch (e) {
            showAlert("error", "Download failed", e.message);
        } finally {
            setExporting(null);
        }
    };

    const exportDrawerPdf = async () => {
        if (!drawerData || drawerData.review.computed_status === "Not Started") return;
        setExporting("drawer-pdf");
        try {
            await downloadFile(`/api/dspr/reports/review/${encodeURIComponent(drawerData.review.id)}/pdf`, `${drawerData.employee.id}.pdf`);
        } catch (e) {
            showAlert("error", "PDF export failed", e.message);
        } finally {
            setExporting(null);
        }
    };

    const exportDrawerExcel = async () => {
        if (!drawerData || drawerData.review.computed_status === "Not Started") return;
        setExporting("drawer-excel");
        try {
            await downloadFile(`/api/dspr/reports/review/${encodeURIComponent(drawerData.review.id)}/excel`, `${drawerData.employee.id}.xlsx`);
        } catch (e) {
            showAlert("error", "Excel export failed", e.message);
        } finally {
            setExporting(null);
        }
    };

    // Smart download - backend returns the PDF on its own if there are no
    // employee uploads, or a ZIP containing the PDF + all uploaded files
    // (filenames preserved with extensions). Frontend reads the actual
    // Content-Disposition the server sets so the saved file has the
    // right name and extension.
    const exportDrawerDownload = async () => {
        if (!drawerData || drawerData.review.computed_status === "Not Started") return;
        setExporting("drawer-download");
        try {
            const url = `/api/dspr/reports/review/${encodeURIComponent(drawerData.review.id)}/download`;
            await downloadFile(url, `${drawerData.employee.id}_response`); // fallback name; server sets real one
        } catch (e) {
            showAlert("error", "Download failed", e.message);
        } finally {
            setExporting(null);
        }
    };

    // ── Render ─────────────────────────────────────────────────────────────
    const totalPages = Math.max(1, Math.ceil(total / pageSize));
    const startRow = total === 0 ? 0 : (page - 1) * pageSize + 1;
    const endRow = Math.min(page * pageSize, total);

    return (
        <div className="dspr-container">
            <div className={`dspr-dashboard ${navSize}`}>
                {/* Mounted INSIDE the dashboard - when this was a sibling of
                    .dspr-dashboard inside the flex .dspr-container, the banner
                    became a flex-row item next to the dashboard and pushed it
                    sideways on every alert. */}
                <AlertsHost />
                {/* Page header — no dspr-rp-region; the region class adds a
                    white card + dashed border around the heading which
                    clashes with the Support_Tickets-style treatment used
                    everywhere else in dAdmin. Body blocks below still use
                    dspr-rp-region for their visual grouping. */}
                <div className="dspr-header-wrapper">
                    <h2>
                        <a className="navbar-brand" href="#">
                            <img src={logo_eagle} alt="dAdmin Logo" style={{ height: 50, objectFit: "contain", marginLeft: -20 }} />
                        </a>
                        SPR - Reports
                    </h2>
                    <p className="dspr-subtitle">
                        Self-review submissions and individual response breakdowns
                    </p>
                </div>

                <div className="dspr-dashboard-content">
                    {/* Cycle selector */}
                    <div className="dspr-rp-region dspr-rp-cycle-row">
                        <label>CYCLE</label>
                        <select
                            value={selectedCycle}
                            onChange={e => setSelectedCycle(e.target.value)}
                            className="dspr-rp-cycle-select">
                            {cycles.length === 0 && <option value="">No cycles available</option>}
                            {cycles.map(c => (
                                <option key={c.id} value={c.id}>
                                    {c.half_label}  ·  {c.status}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Stat cards */}
                    <div className="dspr-rp-region dspr-rp-stats-grid">
                        <StatCard label="TOTAL ASSIGNED" value={stats.total_assigned}
                            sub={stats.total_assigned ? "for this cycle" : ""} color="neutral" />
                        <StatCard label="SUBMITTED" value={stats.submitted}
                            sub={stats.total_assigned
                                ? `${Math.round((stats.submitted / stats.total_assigned) * 100)}% complete`
                                : ""}
                            color="green" />
                        <StatCard label="IN PROGRESS" value={stats.in_progress}
                            color="amber" />
                        <StatCard label="NOT STARTED" value={stats.not_started}
                            color="red" />
                    </div>

                    {/* Filters */}
                    <div className="dspr-rp-region dspr-rp-filters">
                        <div className="dspr-rp-filter-grid">
                            <div className="dspr-rp-field">
                                <label>MANAGER</label>
                                <select value={filters.manager}
                                    onChange={e => setFilters(f => ({ ...f, manager: e.target.value }))}>
                                    <option value="">All managers</option>
                                    {managers.map(m => (
                                        <option key={m.emp_id} value={m.emp_id}>
                                            {m.emp_first_name} {m.emp_last_name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="dspr-rp-field">
                                <label>STATUS</label>
                                <select value={filters.status}
                                    onChange={e => setFilters(f => ({ ...f, status: e.target.value }))}>
                                    {STATUS_OPTIONS.map(o => (
                                        <option key={o.value} value={o.value}>{o.label}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="dspr-rp-field dspr-rp-field-wide">
                                <label>SEARCH</label>
                                <input type="text" placeholder="Name or emp_id…"
                                    value={filters.search}
                                    onChange={e => setFilters(f => ({ ...f, search: e.target.value }))}
                                    onKeyDown={e => { if (e.key === "Enter") applyFilters(); }} />
                            </div>
                        </div>
                        <div className="dspr-rp-filter-actions">
                            <button type="button" className="dspr-rp-btn-link" onClick={clearFilters}>Clear</button>
                            <button type="button" className="dspr-rp-btn-primary" onClick={applyFilters}>Apply filters</button>
                            <div className="dspr-rp-spacer" />
                            <button type="button" className="dspr-rp-btn-export"
                                onClick={exportListExcel}
                                disabled={!!exporting || loading || total === 0}>
                                {exporting === "list-excel" ? "Exporting…" : "📥 Excel (list)"}
                            </button>
                        </div>
                    </div>

                    {/* Table */}
                    <div className="dspr-rp-region dspr-rp-table-wrap">
                        <div className="dspr-rp-table-header">
                            <span>Showing <strong>{startRow}</strong>-<strong>{endRow}</strong> of <strong>{total.toLocaleString()}</strong></span>
                            {loading && <span className="dspr-rp-loading-tag">Loading…</span>}
                        </div>
                        <table className="dspr-rp-table">
                            <thead>
                                <tr>
                                    <th>Employee</th>
                                    <th>Designation</th>
                                    <th>Department</th>
                                    <th>Home Manager</th>
                                    <th>Status</th>
                                    <th>Submitted</th>
                                    <th>Completion</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {!loading && rows.length === 0 ? (
                                    <tr><td colSpan={8} className="dspr-empty">No reviews match the current filters.</td></tr>
                                ) : rows.map(r => {
                                    const s = STATUS_META[r.computed_status] || STATUS_META["Not Started"];
                                    const notStarted = r.computed_status === "Not Started";
                                    // View opens the detail drawer - only meaningful once the
                                    // employee has actually submitted. Drafts (Initiated /
                                    // In Progress) and Not Started rows have nothing to show.
                                    const canView = r.computed_status === "Submitted"
                                                  || r.computed_status === "Approved";
                                    const downloadBusy = exporting === `download-${r.review_id}`;
                                    return (
                                        <tr key={r.review_id} className="dspr-rp-row">
                                            <td>
                                                <div className="dspr-rp-emp-name">{`${r.employee_first_name || ""} ${r.employee_last_name || ""}`.trim()}</div>
                                                <div className="dspr-rp-emp-id">{r.employee_id}</div>
                                            </td>
                                            <td className="dspr-rp-cell-trunc" title={r.employee_job_position || ""}>{r.employee_job_position || "-"}</td>
                                            <td className="dspr-rp-cell-trunc" title={r.employee_department || ""}>{r.employee_department || "-"}</td>
                                            <td className="dspr-rp-cell-trunc">
                                                {r.manager_first_name
                                                    ? `${r.manager_first_name} ${r.manager_last_name || ""}`.trim()
                                                    : "-"}
                                            </td>
                                            <td>
                                                <span className="dspr-rp-pill" style={{ background: s.bg, color: s.fg }}>
                                                    <span className="dspr-rp-pill-dot" style={{ background: s.dot }} />
                                                    {s.label}
                                                </span>
                                            </td>
                                            <td className="dspr-rp-cell-when">{r.submitted_at ? fmtDateTimeIST(r.submitted_at) : "-"}</td>
                                            <td className="dspr-rp-cell-completion">
                                                {r.response_count} / {r.total_questions}
                                            </td>
                                            <td className="dspr-rp-cell-actions">
                                                <button type="button" className="dspr-rp-row-btn"
                                                    disabled={!canView}
                                                    title={canView ? "View responses" : "Review hasn't been submitted yet"}
                                                    onClick={() => openDrawer(r.review_id)}>
                                                    View
                                                </button>
                                                <button type="button" className="dspr-rp-row-btn"
                                                    disabled={notStarted || downloadBusy}
                                                    title={notStarted
                                                        ? "No data - review hasn't started"
                                                        : "Download - PDF only if no employee uploads, otherwise a ZIP with the PDF + uploads"}
                                                    onClick={() => exportRowDownload(r)}>
                                                    {downloadBusy ? "…" : "⬇ Download"}
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>

                        {totalPages > 1 && (
                            <div className="dspr-rp-pagination">
                                <button type="button" disabled={page <= 1} onClick={() => setPage(1)}>« First</button>
                                <button type="button" disabled={page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))}>‹ Prev</button>
                                <span className="dspr-rp-page-info">Page <strong>{page}</strong> of <strong>{totalPages}</strong></span>
                                <button type="button" disabled={page >= totalPages} onClick={() => setPage(p => Math.min(totalPages, p + 1))}>Next ›</button>
                                <button type="button" disabled={page >= totalPages} onClick={() => setPage(totalPages)}>Last »</button>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {selectedReviewId && (
                <DetailDrawer
                    data={drawerData}
                    loading={drawerLoading}
                    onClose={closeDrawer}
                    onExportPdf={exportDrawerPdf}
                    onExportExcel={exportDrawerExcel}
                    onExportDownload={exportDrawerDownload}
                    exporting={exporting}
                />
            )}
        </div>
    );
}

// ════════════════════════════════════════════════════════════════════════════
// STAT CARD
// ════════════════════════════════════════════════════════════════════════════
function StatCard({ label, value, sub, color }) {
    return (
        <div className={`dspr-rp-stat dspr-rp-stat-${color}`}>
            <div className="dspr-rp-stat-label">{label}</div>
            <div className="dspr-rp-stat-num">{(value || 0).toLocaleString()}</div>
            {sub && <div className="dspr-rp-stat-sub">{sub}</div>}
        </div>
    );
}

// ════════════════════════════════════════════════════════════════════════════
// DETAIL DRAWER
// ════════════════════════════════════════════════════════════════════════════
function DetailDrawer({ data, loading, onClose, onExportPdf, onExportExcel, onExportDownload, exporting }) {
    // Tab-based layout: tab strip + active panel. No accordion, no per-section
    // toggle. Defaults to first section; resets to first whenever a different
    // review is loaded into the drawer.
    const [activeTab, setActiveTab] = useState(DSPR_QUESTION_SCHEMA.sections[0].id);
    const loadedReviewId = data?.review?.id;
    useEffect(() => {
        if (loadedReviewId) {
            setActiveTab(DSPR_QUESTION_SCHEMA.sections[0].id);
        }
    }, [loadedReviewId]);

    if (loading || !data) {
        return (
            <>
                <div className="dspr-rp-drawer-backdrop" />
                <div className="dspr-rp-drawer">
                    <div className="dspr-rp-drawer-head">
                        <span>Review detail</span>
                        <button className="dspr-modal-close" onClick={onClose}>✕</button>
                    </div>
                    <div className="dspr-rp-drawer-body"><div className="dspr-empty">Loading…</div></div>
                </div>
            </>
        );
    }

    const { review, employee, manager, cycle, client_project, responses, attachments, response_count, total_questions } = data;
    const s = STATUS_META[review.computed_status] || STATUS_META["Not Started"];
    const isDraft = review.computed_status !== "Submitted";
    const notStarted = review.computed_status === "Not Started";

    return (
        <>
            <div className="dspr-rp-drawer-backdrop" />
            <div className="dspr-rp-drawer dspr-rp-drawer-wide">
                <div className="dspr-rp-drawer-head">
                    <div>
                        <div className="dspr-rp-drawer-eyebrow">{cycle.half_label}</div>
                        <div className="dspr-rp-drawer-title">{`${employee.first_name} ${employee.last_name}`.trim()}</div>
                    </div>
                    <button className="dspr-modal-close" onClick={onClose}>✕</button>
                </div>

                {/* Export bar - Excel / PDF / smart Download */}
                <div className="dspr-rp-drawer-actions">
                    <span className="dspr-rp-pill" style={{ background: s.bg, color: s.fg }}>
                        <span className="dspr-rp-pill-dot" style={{ background: s.dot }} />
                        {s.label}  ·  {response_count}/{total_questions}
                    </span>
                    <div className="dspr-rp-spacer" />
                    <button type="button" className="dspr-rp-btn-export"
                        onClick={onExportExcel}
                        disabled={notStarted || exporting === "drawer-excel"}
                        title={notStarted ? "No data to export" : "Download per-employee Excel"}>
                        {exporting === "drawer-excel" ? "Exporting…" : "📥 Excel"}
                    </button>
                    <button type="button" className="dspr-rp-btn-primary"
                        onClick={onExportPdf}
                        disabled={notStarted || exporting === "drawer-pdf"}
                        title={notStarted ? "No data to export" : "Download response PDF"}>
                        {exporting === "drawer-pdf" ? "Exporting…" : "📥 PDF"}
                    </button>
                </div>

                {/* Draft banner */}
                {isDraft && !notStarted && (
                    <div className="dspr-rp-draft-banner">
                        ⚠ Draft - employee hasn't submitted yet. Content may change.
                    </div>
                )}
                {notStarted && (
                    <div className="dspr-rp-draft-banner dspr-rp-not-started-banner">
                        ○ Not Started - no responses recorded yet.
                    </div>
                )}

                <div className="dspr-rp-drawer-body">
                    {/* Identity card */}
                    <div className="dspr-rp-id-card">
                        <IdRow label="Employee">{`${employee.first_name} ${employee.last_name}`.trim()}</IdRow>
                        <IdRow label="Employee ID">{employee.id}</IdRow>
                        <IdRow label="Designation">{employee.job_position || "-"}</IdRow>
                        <IdRow label="Department">{employee.department || "-"}</IdRow>
                        <IdRow label="Home Manager">{manager ? `${manager.first_name} ${manager.last_name}` : "-"}</IdRow>
                        <IdRow label="Client / Project">{client_project || "-"}</IdRow>
                        <IdRow label="Submitted">{review.submitted_at ? fmtDateTimeIST(review.submitted_at) : "-"}</IdRow>
                        {review.submitted_by_role === "admin" && (
                            <IdRow label="Submitted by">
                                <span className="dspr-rp-admin-proxy-pill">
                                    {review.submitter_name || review.submitted_by_email} <em>(admin)</em>
                                </span>
                                <div className="dspr-rp-admin-proxy-sub">
                                    Filed on behalf of the employee.
                                </div>
                            </IdRow>
                        )}
                    </div>

                    {/* Tab strip - one tab per section. Active gets colored
                        underline + bold text. Click switches active panel. */}
                    <div className="dspr-rp-tabs" role="tablist">
                        {DSPR_QUESTION_SCHEMA.sections.map(section => {
                            const meta = SECTION_META[section.color] || SECTION_META.team;
                            const isActive = activeTab === section.id;
                            return (
                                <button
                                    key={section.id}
                                    type="button"
                                    role="tab"
                                    aria-selected={isActive}
                                    className={`dspr-rp-tab ${isActive ? "active" : ""}`}
                                    style={isActive ? { borderBottomColor: meta.dot, color: meta.dot } : undefined}
                                    onClick={() => setActiveTab(section.id)}>
                                    <span className="dspr-rp-tab-dot" style={{ background: meta.dot }} />
                                    <span className="dspr-rp-tab-name">{section.title}</span>
                                    <span className="dspr-rp-tab-count">({section.questions.length})</span>
                                </button>
                            );
                        })}
                    </div>

                    {/* Active tab panel - ALL questions in that section
                        rendered inline, with answers. Heading at the top
                        echoes which section's Q&A is being shown. */}
                    {(() => {
                        const activeSection = DSPR_QUESTION_SCHEMA.sections.find(s => s.id === activeTab)
                            || DSPR_QUESTION_SCHEMA.sections[0];
                        const meta = SECTION_META[activeSection.color] || SECTION_META.team;
                        return (
                            <div className="dspr-rp-tab-panel" role="tabpanel">
                                <div className="dspr-rp-tab-panel-head"
                                    style={{ borderBottom: `2px solid ${meta.dot}` }}>
                                    <span className="dspr-rp-tab-panel-dot" style={{ background: meta.dot }} />
                                    <span className="dspr-rp-tab-panel-title">{activeSection.title}</span>
                                    <span className="dspr-rp-tab-panel-sub">
                                        {activeSection.subtitle}
                                    </span>
                                </div>
                                {activeSection.questions.map(q => (
                                    <QARow
                                        key={q.id}
                                        question={q}
                                        response={responses[q.id]}
                                        attachments={(attachments || []).filter(a => a.question_id === q.id)}
                                    />
                                ))}
                            </div>
                        );
                    })()}
                </div>
            </div>
        </>
    );
}

function IdRow({ label, children }) {
    return (
        <div className="dspr-rp-id-row">
            <div className="dspr-rp-id-label">{label}</div>
            <div className="dspr-rp-id-value">{children || "-"}</div>
        </div>
    );
}

// ── isAnswered: defensive - returns true only when there's real content.
//    Mirrors generateSelfReviewPdf.js so PDF + drawer agree on "answered".
function isAnswered(q, r) {
    if (!r || typeof r !== "object") return false;
    switch (q.type) {
        case "text":
        case "text_with_upload":
            return !!(r.value && String(r.value).trim());
        case "single_choice":
            return typeof r.choice === "number";
        case "single_choice_with_text":
        case "single_choice_with_text_and_upload":
            return typeof r.choice === "number" || !!(r.text && String(r.text).trim());
        case "rating_1_to_5_with_text":
            return typeof r.rating === "number";
        case "number_pair_with_text":
            return typeof r.num1 === "number" || typeof r.num2 === "number" || !!(r.text && String(r.text).trim());
        default:
            return false;
    }
}

// ── renderAnswerBody - wrapped in try/catch so a single malformed response
//    can never break the entire .map() render. Returns explicit JSX for
//    every type. NEVER returns null/undefined - every branch yields a node.
function renderAnswerBody(question, response) {
    try {
        if (!isAnswered(question, response)) {
            return <div className="dspr-rp-empty">- No response -</div>;
        }
        switch (question.type) {
            case "text":
            case "text_with_upload":
                return <div className="dspr-rp-text-ans">{String(response.value || "")}</div>;
            case "single_choice": {
                const opts = Array.isArray(question.options) ? question.options : [];
                const opt  = opts[response.choice] ?? "-";
                return (
                    <div>
                        <span className="dspr-rp-selected">Selected:</span> {opt}
                    </div>
                );
            }
            case "single_choice_with_text":
            case "single_choice_with_text_and_upload": {
                const opts = Array.isArray(question.options) ? question.options : [];
                const opt  = typeof response.choice === "number"
                    ? (opts[response.choice] ?? "-")
                    : "- Not selected -";
                const hasComment = response.text && String(response.text).trim();
                return (
                    <>
                        <div><span className="dspr-rp-selected">Selected:</span> {opt}</div>
                        {hasComment && (
                            <div className="dspr-rp-sub-block">
                                <div className="dspr-rp-sub-label">COMMENT</div>
                                <div className="dspr-rp-text-ans">{String(response.text)}</div>
                            </div>
                        )}
                    </>
                );
            }
            case "rating_1_to_5_with_text": {
                const r = response.rating;
                const lbl = r != null ? (RATING_LABELS[r] || "-") : "-";
                const hasJust = response.text && String(response.text).trim();
                return (
                    <>
                        <div>
                            <span className="dspr-rp-selected">Rating:</span>{" "}
                            {r != null ? `${r} / 5` : "-"}{" "}
                            <span className="dspr-rp-muted">({lbl})</span>
                        </div>
                        {hasJust && (
                            <div className="dspr-rp-sub-block">
                                <div className="dspr-rp-sub-label">JUSTIFICATION</div>
                                <div className="dspr-rp-text-ans">{String(response.text)}</div>
                            </div>
                        )}
                    </>
                );
            }
            case "number_pair_with_text": {
                const n1 = response.num1, n2 = response.num2;
                const rate = (typeof n1 === "number" && n1 > 0 && typeof n2 === "number")
                    ? Math.round((n2 / n1) * 1000) / 10 : null;
                const targetMet = rate != null && rate >= 95;
                const hasCtx = response.text && String(response.text).trim();
                return (
                    <>
                        <div>
                            <span className="dspr-rp-muted">Scheduled:</span> <strong>{n1 ?? "-"}</strong>
                            {"  ·  "}
                            <span className="dspr-rp-muted">Delivered:</span> <strong>{n2 ?? "-"}</strong>
                            {"  ·  "}
                            <span className="dspr-rp-muted">Rate:</span>{" "}
                            <strong style={{ color: rate == null ? "#6b7384" : (targetMet ? "#15803d" : "#c2840a") }}>
                                {rate != null ? `${rate}%` : "-"}
                                {rate != null && (targetMet ? "  (target met)" : "  (below target)")}
                            </strong>
                        </div>
                        {hasCtx && (
                            <div className="dspr-rp-sub-block">
                                <div className="dspr-rp-sub-label">CONTEXT</div>
                                <div className="dspr-rp-text-ans">{String(response.text)}</div>
                            </div>
                        )}
                    </>
                );
            }
            default:
                return <div className="dspr-rp-empty">- No response -</div>;
        }
    } catch (e) {
        // Stay defensive - never let a single bad response kill the .map().
        return <div className="dspr-rp-empty">(could not render answer: {String(e.message)})</div>;
    }
}

// ── QARow - one question + its answer block. Always renders title + answer
//    body. Used by the active tab panel in DetailDrawer.
function QARow({ question, response, attachments }) {
    return (
        <div className="dspr-rp-qa">
            <div className="dspr-rp-qa-head">
                <span className="dspr-rp-qa-num">Q{question.number}</span>
                <div className="dspr-rp-qa-title">{question.title}</div>
            </div>
            {question.description && (
                <div className="dspr-rp-qa-desc">›  {question.description}</div>
            )}
            <div className="dspr-rp-qa-divider" />
            <div className="dspr-rp-qa-answer">
                {renderAnswerBody(question, response)}
            </div>
            {Array.isArray(attachments) && attachments.length > 0 && (
                <div className="dspr-rp-attachments">
                    <div className="dspr-rp-attach-label">ATTACHMENTS</div>
                    {attachments.map(a => (
                        <AttachmentLink key={a.id} attachment={a} />
                    ))}
                </div>
            )}
        </div>
    );
}

// ── AttachmentLink - fetches the file with credentials (so the admin's JWT
//    cookie is sent), then either opens images inline in a new tab or
//    triggers a download for other types. Sidesteps the broken-direct-href
//    behaviour where the dev server's catch-all serves index.html for
//    /api/* paths.
function AttachmentLink({ attachment }) {
    const [busy, setBusy] = useState(false);
    const { showAlert, AlertsHost } = useDsprAlerts();
    const isImage = String(attachment.mime_type || "").startsWith("image/");

    const handleClick = async (e) => {
        e.preventDefault();
        if (busy) return;
        setBusy(true);
        try {
            const res = await apiFetch(
                `/api/dspr/reports/attachment/${attachment.id}`,
                { credentials: "include" }
            );
            if (!res.ok) {
                let msg = "Couldn't load attachment";
                try { const j = await res.json(); msg = j.error || msg; } catch { /* */ }
                throw new Error(msg);
            }
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            if (isImage) {
                // Open image in a fresh tab - viewer keeps the blob URL alive
                // until the tab is closed.
                window.open(url, "_blank", "noopener");
                // No revoke - the new tab still needs the URL.
            } else {
                // Trigger a download with the original filename
                const dl = document.createElement("a");
                dl.href = url;
                dl.download = attachment.original_name || `attachment-${attachment.id}`;
                dl.click();
                setTimeout(() => URL.revokeObjectURL(url), 1500);
            }
        } catch (err) {
            showAlert("error", "Attachment failed", err.message);
        } finally {
            setBusy(false);
        }
    };

    return (
        <>
            <AlertsHost />
            <a href="#"
               className="dspr-rp-attach-link"
               onClick={handleClick}
               title={isImage ? "Open image in new tab" : "Download file"}>
                {busy ? "…" : "📎"} {attachment.original_name} · {fmtBytes(attachment.size_bytes)}
            </a>
        </>
    );
}

export default DSprReports;
